import { useMemo, useState } from 'react';
import type { DataTableColumnDef } from '../particles/data-table/data-table-types';
import type { IconName } from '../components/ui/icon';
import type { Omittable } from "../lib/props";

// --- Types ---

export type DataTableGroupSortDirection = 'asc' | 'desc';

export type DataTableGroupFieldConfig = Omittable<{
  /** Display label in the group-by picker. */
  label: string;
  /** Icon shown next to the group field in the group-by picker. */
  icon?: IconName;
  /** Predefined groups (key + label). */
  groups?: Array<{ key: string; label: string }>;
  /**
   * Filter key that corresponds to this group field.
   * Used by resolveVisibleGroups to narrow groups when a matching filter is active.
   */
  filterKey?: string;
  /** When true, groups keep their defined order instead of being sorted. */
  preserveOrder?: boolean;
}>;

export type UseDataTableStateConfig<TData> = Omittable<{
  columns: DataTableColumnDef<TData>[];
  groupFields?: Record<string, DataTableGroupFieldConfig>;
  /** Initial group-by field for uncontrolled mode (default: 'none'). */
  defaultGroupByField?: string;
  /** Initial group sort direction for uncontrolled mode (default: 'asc'). */
  defaultGroupSortDirection?: DataTableGroupSortDirection;
  /** Controlled group-by field. When provided, the hook delegates state to the parent. */
  groupByField?: string;
  /** Called when the user changes the group-by field. Required when groupByField is controlled. */
  onGroupByFieldChange?: (field: string) => void;
  /** Controlled group sort direction. */
  groupSortDirection?: DataTableGroupSortDirection;
  /** Called when the user changes the group sort direction. */
  onGroupSortDirectionChange?: (direction: DataTableGroupSortDirection) => void;
}>;

export type UseDataTableStateReturn<TData> = {
  columns: DataTableColumnDef<TData>[];
  groupByField: string;
  setGroupByField: (field: string) => void;
  isGrouped: boolean;
  groupByLabels: Record<string, string>;
  groupByIcons: Partial<Record<string, IconName>>;
  activeGroupConfig: DataTableGroupFieldConfig | undefined;
  groupSortDirection: DataTableGroupSortDirection;
  setGroupSortDirection: (direction: DataTableGroupSortDirection) => void;
};

// --- Hook ---

export function useDataTableState<TData>({
  columns,
  groupFields,
  defaultGroupByField = 'none',
  defaultGroupSortDirection = 'asc',
  groupByField: controlledGroupByField,
  onGroupByFieldChange,
  groupSortDirection: controlledGroupSortDirection,
  onGroupSortDirectionChange,
}: UseDataTableStateConfig<TData>): UseDataTableStateReturn<TData> {
  const [internalGroupByField, setInternalGroupByField] = useState(defaultGroupByField);
  const [internalGroupSortDirection, setInternalGroupSortDirection] =
    useState<DataTableGroupSortDirection>(defaultGroupSortDirection);

  const groupByField = controlledGroupByField ?? internalGroupByField;
  const setGroupByField = onGroupByFieldChange ?? setInternalGroupByField;

  const groupSortDirection =
    controlledGroupSortDirection ?? internalGroupSortDirection;
  const setGroupSortDirection =
    onGroupSortDirectionChange ?? setInternalGroupSortDirection;

  const isGrouped = groupByField !== 'none';

  const groupByLabels = useMemo(() => {
    const labels: Record<string, string> = { none: '' };
    if (groupFields) {
      for (const [key, config] of Object.entries(groupFields)) {
        labels[key] = config.label;
      }
    }
    return labels;
  }, [groupFields]);

  const groupByIcons = useMemo(() => {
    const icons: Partial<Record<string, IconName>> = {};
    if (groupFields) {
      for (const [key, config] of Object.entries(groupFields)) {
        if (config.icon) icons[key] = config.icon;
      }
    }
    return icons;
  }, [groupFields]);

  const activeGroupConfig = useMemo(() => {
    if (!groupFields || groupByField === 'none') return undefined;
    return groupFields[groupByField];
  }, [groupFields, groupByField]);

  return {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  };
}
